import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { BigPlusButton } from "@ui/components/buttons/bigPlusButton";
import { ChapterIcon } from "@ui/components/icons";
import { stack } from "@ui/components/layout";
import { mobile } from "@ui/styles/utils";
import { ChapterEditor } from "./chapterEditor";
import { useEditorController } from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";
import { TitleEditor } from "./titleEditor";

type CourseMainEditorProps = React.HTMLAttributes<HTMLDivElement>;
export const CourseMainEditor: React.FC<CourseMainEditorProps> = ({ ...htmlProps }) => {
	const controller = useEditorController();
	const course = useObservable(controller.course);
	const { trackingService } = useServices();

	const sortBlocks = (result: DropResult): void => {
		const { source, destination } = result;
		if (!destination) {
			return;
		}
		if (source.droppableId === destination.droppableId && source.index === destination.index) {
			return;
		}
		controller.moveSection(source.droppableId, destination.droppableId, source.index, destination.index);
	};

	return (
		<Container {...htmlProps}>
			<TitleEditor />
			<DragDropContext onDragEnd={sortBlocks}>
				{course.content.chapters.map((chapter) => (
					<Droppable droppableId={chapter.id} key={chapter.id}>
						{(droppableProvided) => (
							<>
								<ChapterEditor
									{...droppableProvided.droppableProps}
									droppableRef={droppableProvided.innerRef}
									chapter={chapter}
									sectionPlaceholder={droppableProvided.placeholder}
								/>
							</>
						)}
					</Droppable>
				))}
			</DragDropContext>
			<BigPlusButton
				icon={<ChapterIcon />}
				label={I18n.t("course.editor.chapter.add")}
				onClick={() => {
					trackingService.track("course / add chapter");
					controller.addChapter();
				}}
			/>
			<RequiredData>{I18n.t("course.editor.required_data")}</RequiredData>
		</Container>
	);
};

const Container = styled.div`
	${stack(40, "flex-start", "stretch")};
	@media ${mobile} {
		${stack(20, "flex-start", "stretch")};
	}
`;

const RequiredData = styled.div`
	${({ theme }) => theme.textStyles.tertiary};
	margin-top: 15px;
`;
